import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { FoinnLogo } from "../components/FoinnLogo";
import { paymentsApi } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | error

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const s = await paymentsApi.status(sessionId);
        if (s.payment_status === "paid") {
          await refresh();
          setState("paid");
          return;
        }
        if (["expired", "failed"].includes(s.payment_status) || tries > 12) {
          setState("error");
          return;
        }
      } catch {
        if (tries > 12) {
          setState("error");
          return;
        }
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
      {state === "checking" && (
        <>
          <Loader2 className="h-12 w-12 animate-spin text-[#2d2824]" />
          <p className="text-lg font-semibold text-[#6e655b]" data-testid="payment-checking">
            Confirming your payment…
          </p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="h-16 w-16 text-[#4CAF50]" />
          <h1 className="font-serif text-4xl font-bold text-[#2d2824]" data-testid="payment-success">
            Thank you!
          </h1>
          <p className="max-w-sm text-[#6e655b]">
            Your payment went through — your plan and AI analyses are now active.
          </p>
          <button
            onClick={() => navigate("/")}
            className="mt-2 flex items-center gap-2 rounded-full bg-[#2d2824] px-6 py-3 font-bold text-white"
            data-testid="payment-home"
          >
            <FoinnLogo className="h-5 w-5" rounded="rounded-md" /> Back to Foinn.org
          </button>
        </>
      )}
      {state === "error" && (
        <>
          <XCircle className="h-16 w-16 text-[#e11d48]" />
          <h1 className="font-serif text-3xl font-bold text-[#2d2824]">Payment not confirmed</h1>
          <p className="max-w-sm text-[#6e655b]">
            We couldn't confirm the payment yet. If you were charged it will appear shortly.
          </p>
          <button
            onClick={() => navigate("/")}
            className="mt-2 rounded-full bg-[#2d2824] px-6 py-3 font-bold text-white"
            data-testid="payment-home"
          >
            <FoinnLogo className="h-5 w-5" rounded="rounded-md" /> Back to Foinn.org
          </button>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
      <XCircle className="h-16 w-16 text-[#a89e92]" />
      <h1 className="font-serif text-3xl font-bold text-[#2d2824]">Payment cancelled</h1>
      <p className="max-w-sm text-[#6e655b]">No worries — nothing was charged.</p>
      <button
        onClick={() => navigate("/")}
        className="mt-2 flex items-center gap-2 rounded-full bg-[#2d2824] px-6 py-3 font-bold text-white"
        data-testid="payment-home"
      >
        <FoinnLogo className="h-5 w-5" rounded="rounded-md" /> Back to Foinn.org
      </button>
    </div>
  );
}
